'use client'

import React, { ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import { Quantity } from '@/lib/frontent-api'
import { MobileMapButton } from '@/components/ui/wizard/filter/SearchOptions'
import classNames from 'classnames'
import { CalendarIcon } from '@/components/elements/CalendarIcon'
import { SEARCH_PARAMETERS_UPDATED_EVENT_TYPE } from '@/components/ui/wizard/Wizard'
import { LocationFilterElementType } from '../../../domain-config/domain-config'
import { setLocationFilter } from '@/components/ui/wizard/WizardActions'
import { ConfigContext } from '@/components/common/ConfigContext'
import Skeleton from 'react-loading-skeleton'
import { useDispatch } from 'react-redux'
import { useWizardSelector } from '@/components/ui/wizard/WizardState'
import { useTranslations } from 'next-intl'

const FILTER_LABEL_BOOKABLE_TRANSLATION_ID = '1'

export function PluralizedSearchResultHeaderMessage(props: {
  numberOfResults: Quantity | undefined
}): ReactNode {
  const translate = useTranslations('SearchPage')

  const numberOfOffers = props.numberOfResults?.NumberOfOffers || 0
  const numberOfResults = props.numberOfResults?.NumberOfResults || 0

  function secondVariableNormalSearchResults() {
    const translatedNumberOfResults = translate('numberHotels', {
      quantity: numberOfResults,
    })
    const secondVariable =
      translatedNumberOfResults !== 'numberHotelsSingular'
        ? translatedNumberOfResults
        : numberOfResults
    return secondVariable
  }

  const translatedNumberOfOffers = translate('numberOffers', { quantity: numberOfOffers })

  const firstVariable =
    translatedNumberOfOffers !== 'SINGULAR_TEXT_RESOURCE'
      ? translatedNumberOfOffers
      : numberOfOffers

  return translate('numberOfResults', {
    quantity: firstVariable,
    total: secondVariableNormalSearchResults(),
  })
}

interface SearchResultHeaderProps {
  setDisplayMap?: (showMap: boolean) => void
  bookmarksPage?: boolean
  message: ReactNode
  searchActive?: boolean
}

const INITIAL_MESSAGE_WIDTH = 200

function Message(props: {
  message: ReactNode
  maxWidth: number | undefined
  setMaxWidth: (maxWidth: number) => void
}): ReactElement {
  const spanRef = React.useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (spanRef.current) {
      props.setMaxWidth(
        Math.max(
          spanRef.current.clientWidth + (props.maxWidth !== INITIAL_MESSAGE_WIDTH ? 20 : 0),
          props.maxWidth || 0,
        ),
      )
    }
  }, [spanRef.current])

  return (
    <div
      className="flex min-h-[30px] min-w-[230px] items-center"
      style={{ width: props.maxWidth ? `${props.maxWidth}px` : 'auto' }}
    >
      <span ref={spanRef}>{props.message}</span>
    </div>
  )
}

export default function SearchResultHeader(props: SearchResultHeaderProps): ReactElement {
  const [travelPeriodSet, setTravelPeriodSet] = useState(false)
  const config = useContext(ConfigContext)!
  const dispatch = useDispatch()
  const locationFilter = useWizardSelector((s) => s.locationFilter)
  const selectedOptionPeriods = useWizardSelector((s) => s.selectedOptionPeriods)
  const [maxWidthMessage, setMaxWidthMessage] = useState<number>()
  const tProps = useTranslations('SearchPage.Props')
  const tBookmarks = useTranslations('Bookmarks')

  useEffect(() => {
    function eventListener(): void {
      setTravelPeriodSet(
        !!window.store?.getState().startDate ||
          !!(selectedOptionPeriods && selectedOptionPeriods.length > 0),
      )
    }

    eventListener()

    window.addEventListener(SEARCH_PARAMETERS_UPDATED_EVENT_TYPE, eventListener)
    return () => window.removeEventListener(SEARCH_PARAMETERS_UPDATED_EVENT_TYPE, eventListener)
  }, [selectedOptionPeriods])

  const directlyBookableFilterActive = locationFilter.find(
    (f) => f.type === LocationFilterElementType.Allotment && f.value === '1',
  )

  function toggleDirectlyBookableFilter() {
    const filter = config.searchFilters.additionalFilters['allotment'][0]
    const newFilter = !directlyBookableFilterActive
      ? [...locationFilter, filter]
      : locationFilter.filter((f) => f.type !== filter.type && f.value !== filter.value)

    dispatch(setLocationFilter(newFilter))
  }

  return (
    <div className={classNames({ 'mt-4': props.bookmarksPage })}>
      {!travelPeriodSet && props.bookmarksPage && (
        <div className="mb-3 mt-5 flex w-full items-center rounded bg-white py-3 drop-shadow">
          <CalendarIcon />
          <span className="table-cell align-middle">{tBookmarks('travelPeriodHint')}</span>
        </div>
      )}
      <div
        className={classNames(
          'mb-4 mt-4 flex flex-row flex-wrap items-center justify-between gap-x-6 gap-y-1 font-semibold text-stone-600 lg:mt-2',
        )}
      >
        {!props.bookmarksPage && !props.searchActive && (
          <Message
            message={props.message}
            maxWidth={maxWidthMessage}
            setMaxWidth={setMaxWidthMessage}
          />
        )}

        {!props.bookmarksPage && props.searchActive && (
          <div className="relative box-content h-[30px]" style={{ width: maxWidthMessage }}>
            <Skeleton className="h-full -translate-y-1" />
          </div>
        )}

        {props.bookmarksPage && (
          <h1 className="inline-block text-3xl font-regular text-stone-800">
            {tBookmarks('heading')}
          </h1>
        )}
        <div className="flex h-[30px] items-center">
          {!props.bookmarksPage && (
            <label className="form-check-label align-center hidden items-center gap-2 md:text-lg lg:flex">
              <input
                autoComplete="off"
                className="form-check-input h-5 w-5 cursor-pointer appearance-none rounded-[4px] border-[2px] border-gray-300 bg-white bg-contain bg-center bg-no-repeat align-top leading-7 text-primary transition duration-200 checked:border-primary checked:bg-primary focus:outline-none focus:ring-primary lg:h-5 lg:w-5 "
                type="checkbox"
                checked={!!directlyBookableFilterActive}
                onChange={toggleDirectlyBookableFilter}
              />
              {tProps(FILTER_LABEL_BOOKABLE_TRANSLATION_ID)}
            </label>
          )}
          {props.setDisplayMap && (
            <div className={classNames('h-[30px] lg:flex', { hidden: !props.bookmarksPage })}>
              <MobileMapButton
                onClick={() => props.setDisplayMap!(true)}
                iconSize={props.bookmarksPage ? 'h-[20px] w-[23px]' : undefined}
                additionalClassName={classNames({ 'text-xl': props.bookmarksPage })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
